import React from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { parseQuery, useImmutableState, durations } from '@betgames/bg-tools';
import { TwainGameId } from 'betgames-contract-js';
import { BootstrapSkeleton, DurationUnits, GameStatus } from 'twain-sport-fe-mono';
import { taxes } from 'utils/taxes';
import { useAnalytics } from 'repositories/AnalyticsRepository/useCases';
import { webSocketsService } from 'services/WebSockets';
import { analyticsService } from 'services/AnalyticsService';
import { translationStore } from 'store/Translations';
import { validateAuthParams } from 'utils/validateAuthParams';
import { routes } from 'utils/routes';
import { setupDateFnsLocale } from 'utils/dateFnsLocale';
import { Lobby } from 'routes/Lobby';
import { Game } from 'routes/Game';
import { DEFAULT_LOCALE } from 'constants/constants';
import { BetHistory } from 'routes/BetHistory';
import { ListSkeleton } from 'components/Skeletons';
import { LazyRoute } from 'components/LazyRoute';
import { Header } from 'components/Header';
import { Footer } from 'components/Footer';
import { AuthProtection } from 'components/AuthProtection';
import { auth } from 'store/Auth';
import { IAuthParams, IAuthParsedParams } from 'store/Auth/interfaces';
import { sessionStore } from 'store/Session';
import { getPresetsIds } from 'utils/getPresetsIds';
import { app } from 'store/App';
import { assetsService } from 'services/AssetsService';
import { GameMessage } from 'components/GameMessage';
import { useUpdateMobileClass } from 'hooks/useUpdateMobileClass';
import { useRefreshIframe } from 'hooks/useRefreshIframe';
import { useRestoreScrollPosition } from 'hooks/useRestoreScrollPosition';
import { ScrollToTop } from 'components/ScrollToTop';
import { Theme } from 'enums/Theme';
import { BackgroundLayout } from 'components/BackgroundLayout';
import { IGame } from 'interfaces/IGame';
import { DevBox } from 'components/dev/DevBox';
import { layout } from 'store/Layout';
import { envConfig } from 'constants/envConfig';
import classes from './Root.scss';

interface IState {
    loading: boolean;
    singleGame?: IGame;
}

export const Root: React.FC = () => {
    const t = translationStore.useTranslate();

    const location = useLocation();
    const analytics = useAnalytics(`${location.pathname}${location.search}`);
    const navigate = useNavigate();

    const [state, setState] = useImmutableState<IState>({
        loading: true,
    });

    useUpdateMobileClass();
    useRefreshIframe(!state.loading);
    useRestoreScrollPosition();

    const authenticate = async (params: IAuthParsedParams) => {
        if (location.pathname.includes(routes.integration)) {
            const authResponse = await auth.store.authenticateReverse(location.search);

            if (authResponse?.integration?.partnerCode) {
                app.store.setPartnerCode(authResponse.integration.partnerCode);
            }

            return {
                ...authResponse.integration,
                token: authResponse?.token,
                currency: authResponse?.currency,
                limits: authResponse?.limits,
                settings: authResponse?.settings,
                theme: sessionStore.theme || Theme.DEFAULT,
                dynamicHeight: true,
            };
        }

        if (!params.partnerCode) {
            return {
                locale: params.locale || sessionStore.language || DEFAULT_LOCALE,
                theme: params.theme || sessionStore.theme,
                dynamicHeight: sessionStore.dynamicHeight,
            };
        }

        const authResponse = await auth.store.authenticate(params);

        return {
            locale: params.locale || sessionStore.language || DEFAULT_LOCALE,
            theme: params.theme || sessionStore.theme,
            partnerCode: params.partnerCode,
            token: authResponse?.token,
            homeUrl: params?.homeUrl,
            currency: authResponse?.currency,
            limits: authResponse?.limits,
            settings: authResponse?.settings,
        };
    };

    const init = async (): Promise<void> => {
        const authParams = validateAuthParams(parseQuery<IAuthParams>(location.search));

        const authResponse = await authenticate(authParams);

        sessionStore.setLanguage(authResponse.locale);

        const partnerCode = authResponse.partnerCode || authParams.partnerCode;

        const [initialResponse] = await Promise.all([
            app.store.fetchInitial(),
            translationStore.init(),
            setupDateFnsLocale(authResponse.locale),
        ]);

        if (initialResponse.customCss) {
            assetsService.initCustomCss(authResponse.theme, initialResponse.customCss);
        }

        assetsService.setTheme(authResponse.theme);
        const currency = authResponse ? authResponse.currency : initialResponse.currency;
        const limits = authResponse ? authResponse.limits : initialResponse.limits;

        if (partnerCode) {
            let jwt: string;
            if (authResponse) {
                jwt = authResponse.token;
            }

            sessionStore.initSession({
                languageCode: authResponse.locale,
                currency,
                limits,
                timezone: authParams.timezone,
                settings: authResponse ? authResponse.settings : undefined,
                jwt,
                wsUrl: authParams.wsUrl || GLOBAL_CONFIG.wsUrl,
                oddsFormat: authParams.oddsFormat,
                showBalance: authParams.showBalance,
                showLogo: authParams.showLogo,
                theme: authResponse.theme || authParams.theme,
                dynamicHeight: authResponse.dynamicHeight || authParams.dynamicHeight,
                homeUrl: authResponse.homeUrl || authParams.homeUrl,
                gui: authParams.gui ?? sessionStore.gui,
                platformID: authParams.platformID ?? sessionStore.platformID,
                versions: initialResponse.games.reduce((acc, game) => {
                    acc[game.id] = game.version;
                    return acc;
                }, {} as Record<TwainGameId, string>),
            });
        }

        layout.init(sessionStore);

        webSocketsService.init(
            sessionStore.wsUrl,
            sessionStore.session.jwt,
            getPresetsIds(initialResponse.games),
        );

        // TODO remove after sometime, temporary solution for smooth transition
        if (!sessionStore.limits) {
            sessionStore.setLimits(initialResponse.limits);
        }
        if (!sessionStore.currency) {
            sessionStore.setCurrency(initialResponse.currency);
        }
        if (!sessionStore.visitorId) {
            FingerprintJS.load()
                .then((fpInstance) => fpInstance.get())
                .then((result) => sessionStore.setVisitorId(result.visitorId));
        }

        taxes.configure(initialResponse.taxSetting, currency || sessionStore.currency);

        if (auth.store.value.authenticated) {
            auth.store.refreshTokenWorker(initialResponse.tokenRefreshInterval, !partnerCode);
        }

        if (authParams.gameId) {
            const isGameAvailable = initialResponse.games.some(
                (game) =>
                    game.id === Number(authParams.gameId) && game.status === GameStatus.AVAILABLE,
            );

            if (isGameAvailable) {
                navigate(routes.game(authParams.gameId as unknown as TwainGameId).current);
            }
        }

        setState((draft) => {
            draft.loading = false;
            const games = initialResponse.games.filter(
                (game) => game.status !== GameStatus.DISABLED,
            );
            draft.singleGame = games.length === 1 ? games[0] : null;
        });

        await analytics();
    };

    React.useEffect(() => {
        init();
    }, []);

    React.useEffect(() => {
        analyticsService.trackChangeRoute(location.pathname);
    }, [location]);

    React.useEffect(() => {
        if (!state.loading) {
            durations.setup([
                t.dates(DurationUnits.YEARS),
                t.dates(DurationUnits.MONTHS),
                t.dates(DurationUnits.WEEKS),
                t.dates(DurationUnits.DAYS),
                t.dates(DurationUnits.HOURS),
                t.dates(DurationUnits.MINUTES),
                t.dates(DurationUnits.SECONDS),
            ]);
        }
    }, [state.loading]);

    const renderRedirect = () => {
        if (state.singleGame) {
            return (
                <Route
                    path="/*"
                    element={
                        <Navigate
                            to={
                                state.singleGame.status === GameStatus.COMING
                                    ? routes.game(state.singleGame.id).howToPlay
                                    : routes.game(state.singleGame.id).current
                            }
                            replace
                        />
                    }
                />
            );
        }

        return (
            <>
                <Route
                    path={routes.lobby}
                    element={
                        <LazyRoute>
                            <Lobby />
                        </LazyRoute>
                    }
                />
                <Route path="/*" element={<Navigate to={routes.lobby} replace />} />
            </>
        );
    };

    if (state.loading) {
        return <BootstrapSkeleton />;
    }

    return (
        <div className={classes.container}>
            {!(envConfig.isProductionEnv || envConfig.isIntegrationsEnv || envConfig.isDemoEnv) && (
                <DevBox />
            )}
            <Header />
            <GameMessage />
            <ScrollToTop />
            <BackgroundLayout />
            <div className={classes.content}>
                <Routes>
                    <Route
                        path={routes.game(':gameId').any}
                        element={
                            <LazyRoute>
                                <Game />
                            </LazyRoute>
                        }
                    />
                    <Route
                        path={routes.betHistory}
                        element={
                            <LazyRoute Fallback={<ListSkeleton />}>
                                <AuthProtection>
                                    <BetHistory />
                                </AuthProtection>
                            </LazyRoute>
                        }
                    />
                    {renderRedirect()}
                </Routes>
            </div>
            <Footer />
        </div>
    );
};
